import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Package, MapPin, Truck, CreditCard, User, Mail, Phone, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS = {
  paid: { label: "Paid", cls: "bg-lime-100 text-lime-700 border-lime-200", icon: CheckCircle2 },
  initiated: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  open: { label: "Open", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  expired: { label: "Expired", cls: "bg-rose-100 text-rose-700 border-rose-200", icon: XCircle },
};
const fmt = (s) => { try { return new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };

const Row = ({ icon: Icon, label, value }) => value ? (
  <div className="flex items-start gap-2.5 text-sm">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
    <div><span className="text-slate-400">{label}: </span><span className="text-slate-800">{value}</span></div>
  </div>
) : null;

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => toast.error("Order not found")).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!order) return <div className="py-24 text-center text-slate-500">Order not found.</div>;

  const st = STATUS[order.payment_status] || STATUS[order.status] || { label: order.payment_status || "—", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: Clock };
  const StIcon = st.icon;
  const subtotal = (order.items || []).reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);
  const paid = order.payment_status === "paid";

  const steps = [
    { key: "initiated", label: "Order placed" },
    { key: "paid", label: "Payment received" },
    { key: "fulfilled", label: "Ready to fulfil" },
  ];
  const currentStep = paid ? 2 : 1;

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="order-detail-page">
      <button onClick={() => navigate("/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="order-back-btn"><ArrowLeft className="h-4 w-4" /> All orders</button>

      <div className="bg-slate-900 text-white rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Order #{(order.id || "").slice(0, 8)}</p>
            <h1 className="text-2xl font-bold font-heading mt-1">{order.customer_name || "Unnamed customer"}</h1>
            <p className="text-slate-300 text-sm mt-1">Placed {fmt(order.created_at)}</p>
          </div>
          <div className="text-right">
            <Badge className={`border ${st.cls} gap-1`}><StIcon className="h-3.5 w-3.5" /> {st.label}</Badge>
            <p className="text-2xl font-bold font-heading mt-2">${(order.amount || 0).toFixed(2)} <span className="text-xs text-slate-400 font-normal">{(order.currency || "aud").toUpperCase()}</span></p>
          </div>
        </div>

        {/* status timeline */}
        <div className="mt-6 flex items-center" data-testid="order-timeline">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${i <= currentStep ? "bg-lime-400 text-slate-900" : "bg-slate-700 text-slate-400"}`}>{i < currentStep || (i === currentStep && paid && i === 2) ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</span>
                <span className="text-[10px] text-slate-400 mt-1 whitespace-nowrap">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${i < currentStep ? "bg-lime-400" : "bg-slate-700"}`} />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><User className="h-5 w-5 text-lime-500" /> Customer</h2>
          <Row icon={User} label="Name" value={order.customer_name} />
          <Row icon={Mail} label="Email" value={order.customer_email} />
          <Row icon={Phone} label="Phone" value={order.customer_phone} />
          <Row icon={MapPin} label="Delivery" value={order.delivery_address} />
          <Row icon={CreditCard} label="Billing" value={order.billing_address} />
          <Row icon={Truck} label="Shipping" value={order.shipping_method ? `${order.shipping_method} ($${(order.shipping_amount || 0).toFixed(2)})` : null} />
          <Row icon={User} label="Placed by" value={order.user_email} />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3"><Package className="h-5 w-5 text-lime-500" /> Items</h2>
          <div className="divide-y divide-slate-100">
            {(order.items || []).map((it, i) => (
              <div key={it.part_id || it.name || i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`order-item-${i}`}>
                <span className="text-slate-800">{it.name} <span className="text-slate-400">×{it.quantity}</span></span>
                <span className="text-slate-600">${((it.price || 0) * (it.quantity || 0)).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 mt-2 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Shipping</span><span>${(order.shipping_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between font-bold text-slate-900 text-base pt-1"><span>Total</span><span>${(order.amount || 0).toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
